import i18n from 'i18next';
import { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE, SUPPORTED_LANGUAGES, SupportedLanguages } from '../../constants/language';
import { ApiResult } from '../general.types';
import { useApiClient } from '../api';
import { TranslationData } from './types';
import { transformTranslations } from './transformers';
import { environment } from '../../config/environment';

export const useI18nService = () => {
  const apiClient = useApiClient();

  const loadTranslations = async (lang: SupportedLanguages): Promise<ApiResult<TranslationData> | null> => {
    try {
      const response = await apiClient.get<ApiResult<TranslationData>>(`${environment.expressApiUrl}/api/translations/${lang}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      return null;
    }
  };

  const initializeI18n = async (changeLanguage: SupportedLanguages | null): Promise<ApiResult<TranslationData> | null> => {
    if (!i18n.isInitialized) {

    const options: InitOptions = {
      lng: import.meta.env.MODE === 'development' ? undefined : 'de',
      fallbackLng: FALLBACK_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES,
      interpolation: {
        escapeValue: false
      },
      react: {
        useSuspense: true
      },
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'i18nextLng',
        caches: ['localStorage']
      },
      resources: {}
    };

    // For non-development environments, skip language detection and force German
    const i18nInstance = i18n.use(initReactI18next);
    
    if (import.meta.env.MODE === 'development') {
      i18nInstance.use(LanguageDetector);
    }
    
    await i18nInstance.init(options);

  }

    const translations = await loadLanguage(changeLanguage || getCurrentLanguage() || DEFAULT_LANGUAGE);
    return translations;
  };

  const loadLanguage = async (lang: SupportedLanguages): Promise<ApiResult<TranslationData> | null> => {
    const response = await loadTranslations(lang);
    const translations = transformTranslations(response);
    
    if (!translations) {
      console.error(`Failed to load translations for ${lang}`);
      return null;
    }

    i18n.addResourceBundle(lang, 'translation', translations.translation, true, true);
    return response;
  };

  const changeLanguage = async (lang: SupportedLanguages): Promise<ApiResult<TranslationData> | null> => {
    const translations = await loadLanguage(lang);
    await i18n.changeLanguage(lang);
    
    // Update Cello language when i18n language changes
    try {
      if (typeof window !== 'undefined' && window.Cello) {
        window.Cello("changeLanguage", lang);
        console.log(`[Cello] Changed language to: ${lang}`);
      }
    } catch (error) {
      console.error('[Cello] Error changing language:', error);
    }
    
    return translations;
  };

  const getCurrentLanguage = (): SupportedLanguages => {
    return (i18n.language as SupportedLanguages) || DEFAULT_LANGUAGE;
  };

  const onLanguageChanged = (callback: () => void) => {
    i18n.on('languageChanged', callback);
    return () => i18n.off('languageChanged', callback);
  };

  return {
    initializeI18n,
    changeLanguage,
    getCurrentLanguage,
    onLanguageChanged,
    loadTranslations,
    loadLanguage,
  };
};

export default i18n;
